import React, { useCallback, useEffect, useState } from 'react';
import {
  EllipsisOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography, Select, Row, Col, Card } from 'antd';
import { apiClient, apiErrorMessage } from '../../../utils';
import { Condition, Filter, Grid } from '../../../models';
import { NewFilterPage } from '.';
import { EntityChangeLog } from '../../reports';
import { TableNameColumn, ConditionsViewer, RecordsPreview } from '../../../elements';

export function FiltersPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [gridsLoading, setGridsLoading] = useState(false);
  const [filters, setFilters] = useState<Filter[]>();
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();
  const [previewModalOpen, setPreviewModalOpen] = useState<string>();
  const [datasets, setDatasets] = useState<Grid[]>();
  const [selectedDatasetId, setSelectedDatasetId] = useState<string>();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const loadFilters = useCallback((current: number, pageSize: number) => {
    setLoading(true);
    const params = [];
    if (selectedDatasetId) {
      params.push(`dataSetId=${selectedDatasetId}`);
    }
    const offset = (current - 1) * pageSize;
    params.push(`offset=${offset}`);
    params.push(`limit=${pageSize}`);
    const queryString = params.length ? '?' + params.join('&') : '';

    apiClient
      .get(`filters${queryString}`)
      .then(response => {
        setFilters(response.data?.filters as Filter[]);
        setPagination(prev => ({
          ...prev,
          total: response.data?.total || 0,
        }));
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedDatasetId]);

  useEffect(() => {
    loadDatasets();
  }, []);

  useEffect(() => {
    // Load filters on mount and when dataset changes, starting from page 1
    setPagination({ current: 1, pageSize: 10, total: 0 });
    loadFilters(1, 10);
  }, [selectedDatasetId, loadFilters]);

  const loadDatasets = () => {
    setGridsLoading(true);
    apiClient
      .get('grids')
      .then(response => {
        setDatasets(response.data as Grid[]);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setGridsLoading(false);
      });
  };

  const openAddModal = () => {
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const openPreviewModal = (id: string) => {
    setPreviewModalOpen(id);
  };

  const closePreviewModal = () => {
    setPreviewModalOpen(undefined);
  };

  const onRefreshClick = () => {
    loadFilters(pagination.current, pagination.pageSize);
  };

  const onFilterAdded = () => {
    closeAddModal();
    loadFilters(pagination.current, pagination.pageSize);
  };

  const deleteFilter = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`filters/${id}`)
      .then(() => {
        loadFilters(pagination.current, pagination.pageSize);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const confirmDeleteFilter = (id: string) => {
    const onOk = () => deleteFilter(id);
    Modal.confirm({
      title: 'Filtre sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Filtreyi silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    } else if (e.key === 'del') {
      confirmDeleteFilter(id);
    }
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      <Menu.Item key="del">
        <Text type="danger">Sil</Text>
      </Menu.Item>
    </Menu>
  );

  const tableColumns = [
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, filter: Filter) => (
        <TableNameColumn name={name} description={filter.description} path={'/admin/filters/' + filter.id} />
      ),
    },
    {
      title: 'Veri seti',
      dataIndex: 'grid',
      key: 'dataSet',
      render: (dataSet: Grid) => (dataSet ? dataSet.name : '-'),
    },
    {
      title: 'Koşullar',
      dataIndex: 'conditionsDisplay',
      key: 'conditions',
      render: (conditionsDisplay: Condition) => <ConditionsViewer conditions={conditionsDisplay} />,
    },
    {
      title: '',
      key: 'preview',
      render: (_: any, filter: Filter) => (
        <Button icon={<TableOutlined />} onClick={() => openPreviewModal(filter.id)}>
          Veri önizleme
        </Button>
      ),
    },
    {
      title: '',
      key: 'action',
      render: (_: any, filter: Filter) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(filter.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <FilterOutlined /> Filtreler
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <Button key="add" onClick={openAddModal} icon={<PlusOutlined />} type="primary">
            Yeni filtre
          </Button>,
        ]}
      />
      <Content>
        <Card style={{ marginBottom: '1rem' }}>
          <Row gutter={16}>
            <Col span={6}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Veri seti</label>
              <Select
                placeholder="Veri seti seçin"
                allowClear
                loading={gridsLoading}
                value={selectedDatasetId}
                onChange={value => setSelectedDatasetId(value)}
                showSearch
                filterOption={(input, option) => (option?.children as string)?.toLowerCase().includes(input.toLowerCase())}
                style={{ width: '100%' }}
              >
                {datasets?.map(dataset => (
                  <Select.Option key={dataset.id} value={dataset.id}>
                    {dataset.name}
                  </Select.Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>
        <Table
          columns={tableColumns}
          rowKey={row => row.id}
          dataSource={filters}
          loading={loading}
          scroll={{ x: '100%' }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: (page, pageSize) => {
              const newPageSize = pageSize || pagination.pageSize;
              setPagination(prev => ({
                ...prev,
                current: page,
                pageSize: newPageSize,
              }));
              loadFilters(page, newPageSize);
            },
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            position: ['bottomCenter'],
            showTotal: (total, range) => {
              let start = range[0];
              if (start < 1) {
                start = 1;
              }
              let end: number = range[1];
              if (end === 0) {
                end = total;
              }
              return `Toplam ${total} filtre. ${start}-${end} arası gösteriliyor.`;
            },
          }}
        />
      </Content>
      <Modal title="Yeni filtre" visible={addModalOpen} footer={null} width={'80%'} onCancel={closeAddModal} destroyOnClose={true}>
        <NewFilterPage onSaved={onFilterAdded} />
      </Modal>
      <Modal
        title="Filtre değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'Filter'} recordId={changeLogModalOpen} />
      </Modal>
      <Modal
        title="Veri önizleme"
        visible={previewModalOpen !== undefined}
        footer={null}
        onCancel={closePreviewModal}
        destroyOnClose={true}
        width={'80%'}
      >
        {previewModalOpen &&
          filters &&
          (() => {
            const filter = filters.find(f => f.id === previewModalOpen);
            return <RecordsPreview dataSetId={filter?.grid?.id} conditions={filter?.conditions} />;
          })()}
      </Modal>
    </>
  );
}
